import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, KeyboardArrowDown } from 'drift-ui';
import styles from './style.scss';
import CarrierDirectoryItem from './CarrierDirectoryItem';
import Pagination from '../Pagination/Pagination';
import { useSearchQueryParams } from '../../../utils/hooks';
import { SearchedUser, SortColumns, SortOrder } from '../../../services/usersService/usersServiceQuery';
import PlaceholderCard from './PlaceholderCard';
import Placeholder from '../../../common/components/Placeholder';
import { useSearchURLParams } from '../../../common/utilCustomHooks';
import cx from 'classnames';

type Props = {
  data: SearchedUser[];
  state: string;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (page: number) => void;
};
const CarrierDirectoryList = ({ data, state, totalItems, onRowsPerPageChange, onPageChange }: Props) => {
  const { t } = useTranslation();
  const { getQueryParam } = useSearchQueryParams();
  const sortColumn = (getQueryParam('sort') as SortColumns) ?? SortColumns.Name;
  const sortDirection = (getQueryParam('order') as SortOrder) ?? SortOrder.ASC;

  switch (state) {
    case 'uninitialized':
      return <DefaultState />;
    case 'pending':
      return <LoadingComponent />;
    default:
      if (data.length === 0) {
        return <NotFound />;
      }
      return (
        <>
          <TableHeader sortColumn={sortColumn} sortDirection={sortDirection}>
            <div className={styles['search-banner']}>
              {t('showing_results_for', { count: totalItems })} {getQueryParam('query') || ''}
            </div>
            <div className={styles['table-body']}>
              {data.map((rowData) => (
                <CarrierDirectoryItem key={rowData.email} user={rowData} defaultOpen={data.length === 1} />
              ))}
            </div>
          </TableHeader>
          {totalItems && (
            <div className={styles['pagination-footer']}>
              <Pagination
                totalItems={totalItems}
                onRowsPerPageChange={onRowsPerPageChange}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </>
      );
  }
};

const DefaultState = () => <div className={styles['initial-state']}>Search for a carrier</div>;

const NotFound = () => <div className={styles['initial-state']}>Not found</div>;

const LoadingComponent = () => {
  const { countPerPage: placeholderUsersAmount } = useSearchURLParams();

  const placeholderUsersArray = useMemo(
    () => [...Array(placeholderUsersAmount)].map((v, i) => i),
    [placeholderUsersAmount],
  );
  return (
    <TableHeader>
      <div className={styles['search-banner']}>
        <Placeholder width="20vw" />
      </div>
      <div className={styles['table-body']}>
        {placeholderUsersArray.map((card) => (
          <PlaceholderCard key={card} />
        ))}
      </div>
    </TableHeader>
  );
};

type TableHeaderProps = React.PropsWithChildren & {
  sortDirection?: SortOrder;
  sortColumn?: SortColumns;
};
const TableHeader = ({ children, sortDirection, sortColumn }: TableHeaderProps) => {
  const { t } = useTranslation();
  const { updateQueryParams } = useSearchQueryParams();

  const isSortAscending = sortDirection === SortOrder.ASC;
  const handleSort = (column: SortColumns) => {
    const sortOrder = sortColumn === column && isSortAscending ? SortOrder.DESC : SortOrder.ASC;
    updateQueryParams({ sort: column, order: sortOrder, page: 1 });
  };
  const getColumnSortDirection = (column: SortColumns) =>
    sortColumn === column && sortDirection === SortOrder.DESC ? styles['rotated'] : '';

  return (
    <div className={styles.table}>
      <div className={styles['table-header']}>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>{t('name')}</span>
          <IconButton
            onClick={() => handleSort(SortColumns.Name)}
            className={cx(styles['icon-button'], getColumnSortDirection(SortColumns.Name))}
          >
            <KeyboardArrowDown />
          </IconButton>
        </div>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>Company</span>
          <IconButton
            onClick={() => handleSort(SortColumns.Company)}
            className={cx(styles['icon-button'], getColumnSortDirection(SortColumns.Company))}
          >
            <KeyboardArrowDown />
          </IconButton>
        </div>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>CRM</span>
        </div>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>{t('email')}</span>
          <IconButton
            onClick={() => handleSort(SortColumns.Email)}
            className={cx(styles['icon-button'], getColumnSortDirection(SortColumns.Email))}
          >
            <KeyboardArrowDown />
          </IconButton>
        </div>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>{t('phone')}</span>
        </div>
        <div className={styles['table-header-cell']}>
          <span className={styles['label-header']}>{t('status')}</span>
        </div>
      </div>
      {children}
    </div>
  );
};

export default CarrierDirectoryList;
